import { useCallback, useContext, useEffect, useRef, useState } from "react";
import { AjaxResult } from "../../../../enums/ajaxResult";
import { MaintenanceModeDto } from "../../../../dtos/site";
import { useApi } from "../../../useApi";
import SignalRContext from "../../../../signalr/context";

interface Props {
    dealerId: number | null | undefined;
    orgId?: number | null | undefined;
    siteId?: number | null | undefined;
}

export function useSitesInMaintenance({ dealerId, orgId, siteId }: Props) {
    const { maintenanceModeApi } = useApi();
    const { maintenanceModeStatus } = useContext(SignalRContext);
    const [sites, setSites] = useState<MaintenanceModeDto[]>([]);
    const [loading, setLoading] = useState(true);

    const load = useCallback((options?: { forceRefresh?: boolean }) => {
        // Priority: Site > Org > Dealer
        if (siteId) {
            setLoading(true);
            // Use getStatus for a single site, which returns MaintenanceModeStatusDto
            maintenanceModeApi.getStatus(siteId, options).then((r) => {
                if (r.result === AjaxResult.Success && r.data?.isInMaintenanceMode && r.data.maintenanceMode) {
                    setSites([r.data.maintenanceMode]);
                } else {
                    setSites([]);
                }
                setLoading(false);
            });
        } else if (orgId) {
            setLoading(true);
            maintenanceModeApi.getActiveForOrg(orgId).then((r) => {
                if (r.result === AjaxResult.Success && r.data) {
                    setSites(r.data);
                } else {
                    setSites([]);
                }
                setLoading(false);
            });
        } else if (dealerId) {
            setLoading(true);
            maintenanceModeApi.getActiveForDealer(dealerId).then((r) => {
                if (r.result === AjaxResult.Success && r.data) {
                    setSites(r.data);
                } else {
                    setSites([]);
                }
                setLoading(false);
            });
        } else {
            setLoading(true);
            maintenanceModeApi.getActiveAll().then((r) => {
                if (r.result === AjaxResult.Success && r.data) {
                    setSites(r.data);
                } else {
                    setSites([]);
                }
                setLoading(false);
            });
        }
    }, [maintenanceModeApi, dealerId, orgId, siteId]);

    useEffect(() => {
        load();

        // Refresh every 60 seconds as fallback
        const interval = setInterval(load, 60000);

        return () => clearInterval(interval);
    }, [load]);

    // Refresh when SignalR maintenance mode status changes (skip the initial mount,
    // already handled by the effect above)
    const isFirstRender = useRef(true);
    useEffect(() => {
        if (isFirstRender.current) {
            isFirstRender.current = false;
            return;
        }
        load({ forceRefresh: true });
    }, [maintenanceModeStatus, load]);

    return { sites, loading, reload: load };
}
